import React, { useCallback, useState } from 'react';
import { FormSpy } from 'react-final-form';
import { FormApi, FormState } from 'final-form';
import { FieldArray } from 'react-final-form-arrays';
import arrayMutators from 'final-form-arrays';
import { useHistory } from 'react-router-dom';
import * as yup from 'yup';
import { CircularProgress } from '@material-ui/core';
import ErrorModal from 'components/modals/ErrorModal/ErrorModal';
import { getTransactionsArray } from 'utils/mapping/transactionCardMapper';
import noop from 'utils/noop';
import DashboardCard from 'ui/DashboardCard/DashboardCard';
import { ITransactionField } from 'ui/structure/TransactionCard';
import routePaths from 'routes/routePaths';
import { WarningModal, StatusModal, OTPConfirmationModal } from 'components/modals';
import Form from 'ui/Form/Form';
import Button from 'ui/Button/Button';
import { AxiosStatus } from 'api/axiosConfig';
import FieldsLeveler from 'ui/structure/FieldsLeveler/FieldsLeveler';
import SubmitLeveler from 'ui/structure/SubmitLeveler/SubmitLeveler';
import { NETWORK_REQUIRED } from 'utils/constants';
import api from 'api/api';
import validateOtpPin, { handlePinError } from 'utils/otpValidation';
import { IOTPError } from 'components/authModules/LogIn/login.model';
import { requiredOnly, validationCallback, validateAmount } from 'utils/validation';
import { IAccount } from 'store/accounts.model';
import { IApiResponse } from 'utils/models';
import FieldsInputsLever from 'ui/structure/FieldsInputsLever/FieldsInputsLever';
import { changeFieldValue, setFieldTouched } from 'utils/formMutators';
import { validatePrepardCard } from '../../../SingleCardLoad/submitApi';
import {
  IFormValues,
  bulkDataConfig,
  bulkSortArray,
  ICardLoadResponse,
  IHandleFieldsChange,
} from './ManualEntry.model';
import { renderSourceAccountField, renderManualEntryToFields } from './ManualEntryFields';

// eslint-disable-next-line css-modules/no-unused-class
import styles from './ManualEntry.module.css';

import plus from 'assets/icons/plus.svg';
import trash from 'assets/icons/trash.png';

const warnings = {
  MAX_BULK: 'You’ve reached a maximum of 50 entries.',
  INSUFFICIENT_FUNDS: 'You have insuffient balance for this transaction',
};

const schema = yup.object().shape({
  sourceAccount: requiredOnly('Source Account'),
  transferData: yup.array().of(
    yup.object().shape({
      narration: requiredOnly('Narration'),
      amount: validateAmount(),
      customerId: requiredOnly('Customer ID'),
      cardLastDigit: requiredOnly('Card last four digit'),
      beneficiaryName: requiredOnly('Card Name'),
      phoneNumber: requiredOnly('PhoneNumber'),
    }),
  ),
});

interface IProps {
  accounts: IAccount[];
  showMainForm: boolean;
  handleChangeForm: () => void;
}

const initialValues = {
  sourceAccount: '',
  transferData: [
    {
      amount: '',
      narration: '',
      customerId: '',
      cardLastDigit: '',
      beneficiaryName: '',
      phoneNumber: '',
    },
  ],
};

const ManualEntry = ({ accounts, showMainForm, handleChangeForm }: IProps) => {
  const [submitStatus, setSubmitStatus] = useState<AxiosStatus>(AxiosStatus.DEFAULT);
  const submitting = submitStatus === AxiosStatus.PENDING;
  const [OTPError, setOTPError] = useState<IOTPError | null>(null);

  const [isConfirmationOpen, setConfirmationOpen] = useState(false);
  const [previewData, setPreviewData] = useState<ITransactionField[]>([]);
  const [errorMsg, setErrorMsg] = useState<string>('');
  const [successMsg, setSuccessMsg] = useState<string>('');
  const showStatusModal = submitStatus === AxiosStatus.SUCCESS || submitStatus === AxiosStatus.FAILURE;
  const [simpleWarning, setSimpleWarning] = useState<string>();

  const [errorModal, setErrorModal] = useState<string>();
  const closeErrorModal = () => {
    setErrorModal(undefined);
  };

  let mainForm!: FormApi<IFormValues, Partial<IFormValues>>;

  const validate = useCallback((values: IFormValues) => validationCallback<unknown>(schema)(values), []);

  const history = useHistory();
  const handleOnSuccess = () => {
    history.push(routePaths.dashboard);
  };

  const onChangeForm = useCallback(
    ({ dirty }: FormState<IFormValues>) => {
      if (dirty) {
        handleChangeForm();
      }
    },
    [handleChangeForm],
  );

  const openConfirmation = () => setConfirmationOpen(true);
  const closeConfirmation = () => {
    if (submitStatus === AxiosStatus.SUCCESS) {
      handleOnSuccess();
    } else {
      setConfirmationOpen(false);
    }
  };

  const onCloseStatusModal = () => {
    if (submitStatus === AxiosStatus.SUCCESS) {
      handleOnSuccess();
    }
    setSubmitStatus(AxiosStatus.DEFAULT);
  };

  const onResend = () => api.post('/customer/otp/generate');

  const onRejectedSubmit = useCallback((error: IApiResponse) => {
    console.error(error || NETWORK_REQUIRED);
    setErrorMsg(error.description || NETWORK_REQUIRED);
    setSubmitStatus(AxiosStatus.FAILURE);
    closeConfirmation();
  }, []);

  const getPrincipalAmount = (values: IFormValues) => {
    const transferList = values.transferData;

    if (transferList.length > 0) {
      const result = transferList.map((item) => Number(item.amount)).reduce((a, b) => a + b, 0);
      return result;
    }
    return 0;
  };

  const handleProcessBulkList = (values: IFormValues) => {
    const account = accounts.find((a) => a.accountNumber === values.sourceAccount);

    if (Number(account?.availableBalance) < getPrincipalAmount(values)) {
      setSubmitStatus(AxiosStatus.FAILURE);
      setErrorMsg(`Transfer failed.
      Insufficient funds to complete the transaction.`);
      return;
    }

    const readyValues = {
      sourceAccount: values.sourceAccount,
      numberOfTransactions: values.transferData.length,
      totalAmount: getPrincipalAmount(values),
    };

    setPreviewData(getTransactionsArray(readyValues, bulkDataConfig, undefined, bulkSortArray));
    setSubmitStatus(AxiosStatus.PENDING);
    api
      .post('/customer/otp/generate')
      .then(() => {
        setSubmitStatus(AxiosStatus.DEFAULT);
        openConfirmation();
      })
      .catch((data) => {
        onRejectedSubmit(data);
      });
    openConfirmation();
  };

  const handleFieldsChange: IHandleFieldsChange = () => {
    const value = mainForm.getState().values;
    const { transferData } = value;

    transferData.forEach((currentTransfer, index) => {
      const data = currentTransfer.customerId;

      if (data) {
        setSubmitStatus(AxiosStatus.PENDING);

        validatePrepardCard(data)
          .then((response) => {
            const { preferredNameField, mobilePhoneNumberField } = response.customerResponse;

            mainForm.mutators.changeFieldValue(`transferData[${index}].beneficiaryName`, preferredNameField);
            mainForm.mutators.changeFieldValue(`transferData[${index}].phoneNumber`, mobilePhoneNumberField);
            setSubmitStatus(AxiosStatus.DEFAULT);
          })
          .catch((error) => {
            console.error(error);
          })
          .finally(() => setSubmitStatus(AxiosStatus.DEFAULT));
      }
    });
  };

  const onConfirm = useCallback(
    (values: IFormValues) => (otp: string, saveBeneficiary: boolean) => {
      setErrorMsg('');
      setSubmitStatus(AxiosStatus.PIN_PENDING);

      return validateOtpPin(otp)
        .then(() => {
          setSubmitStatus(AxiosStatus.PENDING);
          const data = values.transferData.map((item) => ({
            ...item,
            description: 'success',
            code: 0,
            amount: Number(item.amount),
            transactionType: 'PREPAIDCARD-TRANSFER',
          }));
          const requestBody = {
            sourceAccount: values.sourceAccount,
            transferData: JSON.stringify(data),
            password: otp,
            passwordType: 'OTP',
            saveBen: saveBeneficiary,
          };
          api
            .post('transfer/manualCardPrepared', requestBody)
            .then((response: ICardLoadResponse) => {
              setSuccessMsg(response.description || 'Transfer Completed Successfully!');
              closeConfirmation();
              setSubmitStatus(AxiosStatus.SUCCESS);
            })
            .catch((error: IApiResponse) => {
              console.error(error);
              setErrorMsg(error.description || 'Transfer is failed.\nUnable to complete the transaction.');
              setSubmitStatus(AxiosStatus.FAILURE);
              closeConfirmation();
            });
        })
        .catch((error: IOTPError) => {
          setOTPError(error);
          handlePinError(setSubmitStatus, setErrorMsg);
        });
    },
    [accounts],
  );

  const onRetryConfirm = () => setSubmitStatus(AxiosStatus.DEFAULT);

  const closeSimpleWarning = useCallback(() => setSimpleWarning(undefined), []);

  const subTitleItem = (
    <div>
      <p className={styles.subTitleItem}>Fill in the form below to create a list of bulk beneficiaries</p>
    </div>
  );

  return (
    <>
      {showMainForm && (
        <DashboardCard className={styles.bulkListCard}>
          <Form
            onSubmit={handleProcessBulkList}
            validate={validate}
            initialValues={initialValues}
            mutators={{
              // potentially other mutators could be merged here
              changeFieldValue,
              setFieldTouched,
              ...arrayMutators,
            }}
            render={({ handleSubmit, form, values, hasValidationErrors }) => {
              mainForm = mainForm || form;
              return (
                <form onSubmit={handleSubmit}>
                  <FieldsLeveler label="Source Account">{renderSourceAccountField(accounts, submitting)}</FieldsLeveler>
                  <FieldsLeveler label="Beneficiary List" subTitle={subTitleItem}>
                    {' '}
                  </FieldsLeveler>
                  <FieldArray name="transferData">
                    {({ fields }) => (
                      <>
                        {fields.map((name: string, index: number) => (
                          <div key={name} className={styles.fields}>
                            <div className={styles.fieldsContainer}>
                              <FieldsInputsLever>
                                {renderManualEntryToFields(
                                  name,
                                  values,
                                  accounts,
                                  form,
                                  submitting,
                                  handleFieldsChange,
                                )}
                              </FieldsInputsLever>
                            </div>
                            {fields.length && fields.length > 1 ? (
                              <div
                                className={styles.headerButton}
                                role="button"
                                tabIndex={0}
                                onClick={() => fields.remove(index)}
                                onKeyPress={noop}
                              >
                                <img src={trash} alt="delete icon" width="40" height="40" />
                              </div>
                            ) : null}
                          </div>
                        ))}
                      </>
                    )}
                  </FieldArray>
                  {values.transferData.length === 50 ? (
                    <p>{warnings.MAX_BULK}</p>
                  ) : (
                    <div
                      className={styles.addBeneficiaryButton}
                      role="button"
                      tabIndex={0}
                      onKeyPress={noop}
                      onClick={() => form.mutators.push('transferData')}
                    >
                      <img src={plus} alt="delete icon" />
                      <p>Add one more beneficiary</p>
                    </div>
                  )}
                  <SubmitLeveler>
                    <Button type="submit" disabled={submitting || hasValidationErrors} fullWidth>
                      {submitting ? <CircularProgress size={20} color="inherit" /> : 'Proceed'}
                    </Button>
                  </SubmitLeveler>
                  <OTPConfirmationModal
                    open={isConfirmationOpen}
                    data={previewData}
                    onClose={closeConfirmation}
                    onResend={onResend}
                    OTPPinError={OTPError}
                    onSubmit={onConfirm(values)}
                    submitStatus={submitStatus}
                    onSuccess={handleOnSuccess}
                    onRetry={onRetryConfirm}
                    successText={successMsg}
                    failureText={errorMsg}
                    // {...(state.extras.destinationIsExistingBen
                    //   ? { disableSaveBeneficiary: true, defaultSaveBeneficiary: false }
                    //   : {})}
                  />
                  <StatusModal
                    open={showStatusModal}
                    status={submitStatus === AxiosStatus.SUCCESS ? 'success' : 'failure'}
                    onClose={onCloseStatusModal}
                    onRetry={onCloseStatusModal}
                    onRetryText="Close"
                  >
                    {submitStatus === AxiosStatus.SUCCESS ? successMsg : (
                      <div style={{ whiteSpace: 'pre-line' }}>{errorMsg}</div>
                    )}
                  </StatusModal>
                  <FormSpy subscription={{ values: true, dirty: true }} onChange={onChangeForm} />
                  <WarningModal open={!!simpleWarning} onClose={closeSimpleWarning} cancelName="Close">
                    {simpleWarning}
                  </WarningModal>
                  <ErrorModal open={!!errorModal} onClose={closeErrorModal}>
                    {errorModal}
                  </ErrorModal>
                </form>
              );
            }}
          />
        </DashboardCard>
      )}
    </>
  );
};

export default ManualEntry;
